import { useEffect, useMemo, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { initials, type DocumentOwnerEntry, type ReadinessRow } from './documentReadinessHelpers';
import type { UserOut } from '@/types/api';

interface EditOwnersProps {
  open: boolean;
  doc: ReadinessRow | null;
  users: UserOut[];
  onClose: () => void;
  onSave: (owners: DocumentOwnerEntry[]) => void;
}

/**
 * Edit-owners modal. Admin/lead only — gating is enforced by the caller
 * (button only renders for those roles). Picker source is the active
 * org's members, fetched via api.orgs.members(activeOrgId) by the parent
 * page (the same endpoint Settings → Manage users uses).
 */
export function DocumentReadinessEditOwners({
  open,
  doc,
  users,
  onClose,
  onSave,
}: EditOwnersProps) {
  const [selected, setSelected] = useState<DocumentOwnerEntry[]>([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (doc) setSelected(doc.docOwners.map((o) => ({ ...o })));
    else setSelected([]);
    setQuery('');
  }, [doc]);

  const filtered = useMemo(() => {
    const active = users.filter((u) => u.isActive);
    if (!query) return active;
    const q = query.toLowerCase();
    return active.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q),
    );
  }, [users, query]);

  if (!doc) return null;

  const isSelected = (id: string) => selected.some((o) => o.userId === id);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.some((o) => o.userId === id)
        ? prev.filter((o) => o.userId !== id)
        : [...prev, { userId: id, state: 'pending' }],
    );
  };

  return (
    <div
      className={`fixed inset-0 z-40 flex items-center justify-center bg-black/30 transition-opacity ${
        open ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Edit document owners"
        onClick={(e) => e.stopPropagation()}
        className="flex w-[520px] max-w-[92vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        style={{ maxHeight: '80vh' }}
      >
        <div className="border-b border-slate-200 px-5 pt-4 pb-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">
              Edit document owners
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-7 w-7 place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <h2 className="mt-1 text-lg font-bold text-brand-charcoal">{doc.name}</h2>
          <p className="mt-1 font-mono text-xs text-slate-500">{doc.section}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Search Settings → Manage users
          </label>
          <div className="mb-2.5 flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Name, email, or role..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full border-0 bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="overflow-hidden rounded-md border border-slate-200">
            {filtered.length === 0 ? (
              <p className="py-4 text-center text-xs text-slate-400">No matches.</p>
            ) : (
              filtered.map((u) => {
                const sel = isSelected(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(u.id)}
                    className={`flex w-full items-center gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 transition-colors ${
                      sel ? 'bg-orange-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${
                        sel
                          ? 'border-brand-orange bg-brand-orange text-white'
                          : 'border-slate-300 text-transparent'
                      }`}
                    >
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                    <span
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-500 text-[10px] font-semibold text-white"
                      title={u.displayName}
                    >
                      {initials(u.displayName)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-brand-charcoal">
                        {u.displayName}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-slate-500">
                        {u.role} · {u.email}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <p className="mt-2.5 text-[11px] text-slate-500">
            {selected.length} owner{selected.length === 1 ? '' : 's'} selected. Only users from{' '}
            <b className="font-semibold text-brand-charcoal">Settings → Manage users</b> are
            eligible.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(selected)}
            className="rounded-md bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
